/**
 * use-presence.ts — Trạng thái trực tuyến nhân viên + Nhật ký trạng thái +
 * Cài đặt tự động phân bổ hội thoại.
 *
 * Bảng trạng thái cả tổ chức nằm ở query ['presence'] và được vá trực tiếp bằng
 * sự kiện socket `presence:changed` — chấm màu ở menu, Đội ngũ, bảng phân bổ
 * nhảy ngay không cần tải lại.
 */
import { useEffect } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { getSocket } from '@/lib/socket'
import { applyPresenceEvent, isPresenceStatus, type PresenceStatus } from '@/lib/presence'
import type { ReceptionMode } from '@/lib/channel-access'
import { toAuditParams, type AuditFilters } from '@/pages/settings/audit-lib'
import type { AuditActor, Paged } from './use-audit'

export const presenceKeys = {
  all: ['presence'] as const,
  me: ['presence', 'me'] as const,
  logs: (f: AuditFilters) => ['audit', 'presence', f] as const,
  autoAssign: ['conversation-settings', 'auto-assign'] as const,
}

export type PresenceMap = Record<string, PresenceStatus>

/** userId → trạng thái của mọi nhân viên trong tổ chức. */
export function usePresenceMap(enabled = true) {
  return useQuery({
    queryKey: presenceKeys.all,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ members: { userId: string; status: string }[] }>('/presence')
      const map: PresenceMap = {}
      for (const m of data.members) if (isPresenceStatus(m.status)) map[m.userId] = m.status
      return map
    },
  })
}

/**
 * Giữ kết nối realtime suốt phiên (mở app = Trực tuyến) và vá bảng trạng thái
 * theo `presence:changed`. Gắn MỘT lần ở khung ứng dụng (menu tài khoản).
 */
export function usePresenceRealtime(userId: string | undefined) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!userId) return
    const socket = getSocket()
    const onChanged = (ev: { userId?: string; status?: string }) => {
      qc.setQueryData<PresenceMap>(presenceKeys.all, (prev) => applyPresenceEvent(prev, ev))
      if (ev.userId === userId) qc.invalidateQueries({ queryKey: presenceKeys.me })
    }
    // Kết nối lại sau rớt mạng: trạng thái người khác có thể đã đổi trong lúc mất sóng.
    const onConnect = () => qc.invalidateQueries({ queryKey: presenceKeys.all })
    socket.on('presence:changed', onChanged)
    socket.on('connect', onConnect)
    return () => {
      socket.off('presence:changed', onChanged)
      socket.off('connect', onConnect)
    }
  }, [qc, userId])
}

export function useMyPresence(enabled = true) {
  return useQuery({
    queryKey: presenceKeys.me,
    enabled,
    queryFn: async () =>
      (await api.get<{ status: PresenceStatus; manualStatus: 'busy' | 'offline' | null }>('/presence/me')).data,
  })
}

export function useSetMyPresence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (status: PresenceStatus) =>
      (await api.put<{ status: PresenceStatus; manualStatus: 'busy' | 'offline' | null }>('/presence/me', { status })).data,
    onSuccess: (data) => {
      qc.setQueryData(presenceKeys.me, data)
      qc.invalidateQueries({ queryKey: presenceKeys.all })
    },
  })
}

// ── Nhật ký trạng thái trực tuyến ────────────────────────────────────

export interface PresenceLogItem {
  id: string
  userId: string
  user: (AuditActor & { avatarUrl?: string | null }) | null
  status: PresenceStatus
  statusLabel: string
  reason: string
  reasonLabel: string
  startedAt: string
  endedAt: string | null
  ongoing: boolean
  durationSec: number | null
  durationLabel: string
}

export function usePresenceLogs(f: AuditFilters, enabled = true) {
  return useQuery({
    queryKey: presenceKeys.logs(f),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.get<Paged<PresenceLogItem>>('/audit/presence', { params: toAuditParams(f) })).data,
  })
}

// ── Cài đặt hội thoại → Tự động phân bổ ──────────────────────────────

export type AutoAssignStrategy = 'round_robin' | 'least_busy'

export interface AutoAssignSettings {
  strategy: AutoAssignStrategy
  defaultLimit: number
  memberLimits: Record<string, number>
  aiFirst: boolean
  keepBotOnAssign: boolean
  includeGroups: boolean
  activeWindowHours: number
  backlogOnOnline: boolean
  /** Khách cũ nhắn lại → ưu tiên người phụ trách cũ nếu đang trực tuyến, có quyền kênh, chưa đầy tải. */
  preferPreviousAssignee: boolean
  /** Chủ sở hữu / quản trị / Quản lý kênh cũng nhận chia tự động (tắt = chỉ người Tiếp nhận). */
  includeManagers: boolean
}

export interface AutoAssignMember {
  id: string
  fullName: string
  email: string
  avatarUrl: string | null
  role: string
  status: PresenceStatus
  openCount: number
  limit: number
  customLimit: boolean
}

export interface AutoAssignData {
  settings: AutoAssignSettings
  members: AutoAssignMember[]
  channels: AutoAssignChannel[]
}

export interface AutoAssignChannel {
  id: string
  displayName: string | null
  platform: number
  receptionMode: ReceptionMode
  /** Tự thu hồi hội thoại nếu người nhận không trả lời sau N phút; null = tắt. */
  reclaimAfterMinutes: number | null
  /** Số nhân viên Quản lý kênh / Tiếp nhận của kênh. */
  access: { manage: number; agent: number }
}

export function useAutoAssign() {
  return useQuery({
    queryKey: presenceKeys.autoAssign,
    queryFn: async () => (await api.get<AutoAssignData>('/conversation-settings/auto-assign')).data,
  })
}

export function useSaveAutoAssign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<AutoAssignSettings>) =>
      (await api.put<{ settings: AutoAssignSettings }>('/conversation-settings/auto-assign', patch)).data.settings,
    onSuccess: () => qc.invalidateQueries({ queryKey: presenceKeys.autoAssign }),
  })
}

/** Đặt "tự thu hồi nếu không trả lời sau N phút" cho một hoặc nhiều kênh (null = tắt). Lưu ngay. */
export function useSaveReclaim() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reclaim: Record<string, number | null>) =>
      (await api.put<{ changed: number }>('/conversation-settings/reclaim', { reclaim })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: presenceKeys.autoAssign }),
  })
}

/** Đặt chế độ nhận hội thoại cho một hoặc nhiều kênh (lưu ngay, không qua nút Lưu của cài đặt chung). */
export function useSaveReceptionModes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (modes: Record<string, ReceptionMode>) =>
      (await api.put<{ changed: number }>('/conversation-settings/reception-modes', { modes })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: presenceKeys.autoAssign }),
  })
}
